import * as i0 from '@angular/core';
import { EventEmitter, Component, ChangeDetectionStrategy, Input, Output, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { coerceBooleanProperty, coerceNumberProperty } from '@roosevelt/common-ui-lib/core';
import { IconComponent, IconModule } from '@roosevelt/common-ui-lib/icon';
import { ButtonComponent, ButtonModule } from '@roosevelt/common-ui-lib/button';
import { SelectComponent, SelectModule } from '@roosevelt/common-ui-lib/select';

class PageEvent {
    constructor() {
        this.pageIndex = undefined;
        this.previousPageIndex = undefined;
        this.pageSize = undefined;
        this.length = undefined;
    }
}
/**
 * Paginator Component
 *
 * @description When there are pages of data use this component to show pieces
 * @example
 * <!-- rs-doc {link: 'Example for paginator with basic, 100 and 10'} -->
 * <rs-paginator color="basic" [length]="100" pageSize="10"></rs-paginator>
 * @example
 * <!-- rs-doc {link: 'Example with less than a full page'} -->
 * <rs-paginator [length]="2" pageSize="10"></rs-paginator>
 * @example
 * <!-- rs-doc {link: 'Example for paginator with primary, length, pageSizeOptions and showFirstLastButtons'} -->
 * <rs-paginator [length]="100" [pageSizeOptions]="[5,10,25]" showFirstLastButtons></rs-paginator>
 * @example
 * <!-- rs-doc {link: 'Example for paginator with secondary, length and hidePageSize'} -->
 * <rs-paginator color="secondary" [length]="100" hidePageSize></rs-paginator>
 * @example
 * <!-- rs-doc {link: 'Example for paginator with no length'} -->
 * <rs-paginator [length]="0" ></rs-paginator>
 * @example
 * <!-- rs-doc {link: 'Example for paginator with length, pageSizeOptions and index'} -->
 * <rs-paginator [length]="13" [pageSizeOptions]="[10]" pageIndex="1"></rs-paginator>
 * @example
 * <!-- rs-doc {link: 'Example for paginator with all page option'} -->
 * <rs-paginator [length]="13" [pageSizeOptions]="[{label: 10, value: 10}, {label: 'All', value: 13}]" pageIndex="1"></rs-paginator>
 */
class PaginatorComponent {
    /** Allows the host component to change colors, based on a theme */
    get color() {
        return this._color;
    }
    set color(value) {
        this._getHostElement().classList.add(value);
        if (this._color !== value) {
            this._getHostElement().classList.remove(this._color);
        }
        this._color = value;
    }
    /** template driven way to apply disabled */
    get disabled() {
        return this._disabled;
    }
    set disabled(value) {
        this._disabled = coerceBooleanProperty(value);
    }
    /** sets the page to something other than the default, 0 */
    get pageIndex() { return this._pageIndex; }
    set pageIndex(value) {
        this._pageIndex = Math.max(coerceNumberProperty(value), 0);
        this._changeDetectorRef.markForCheck();
    }
    /** the total amount of items in the set */
    get length() { return this._length; }
    set length(value) {
        this._length = coerceNumberProperty(value);
        if (this.length && this.pageSize !== undefined) {
            this.maxPagesIndex = Math.ceil(this.length / this.pageSize) - 1;
        }
        this._changeDetectorRef.markForCheck();
    }
    /** current page size amount */
    get pageSize() { return this._pageSize; }
    set pageSize(value) {
        this._pageSize = Math.max(coerceNumberProperty(value), 0);
        this._updateDisplayedPageSizeOptions();
        if (this.length && this.pageSize !== undefined) {
            this.maxPagesIndex = Math.ceil(this.length / this.pageSize) - 1;
        }
        this._changeDetectorRef.markForCheck();
    }
    /** you can pass in an array to set a custom list of page sizes */
    get pageSizeOptions() { return this._pageSizeOptions; }
    set pageSizeOptions(value) {
        if (value.length && value[0].value) {
            this._pageSizeOptions = value;
        }
        else {
            this._pageSizeOptions = (value || []).map(p => coerceNumberProperty(p)).map(p => ({ label: p, value: p }));
        }
        this._updateDisplayedPageSizeOptions();
        this._changeDetectorRef.markForCheck();
    }
    /** should hide page size, default is shown */
    get hidePageSize() { return this._hidePageSize; }
    set hidePageSize(value) {
        this._hidePageSize = coerceBooleanProperty(value);
    }
    /** should show first and last buttons, default is hidden */
    get showFirstLastButtons() { return this._showFirstLastButtons; }
    set showFirstLastButtons(value) {
        this._showFirstLastButtons = coerceBooleanProperty(value);
    }
    constructor(_changeDetectorRef, elementRef) {
        this._changeDetectorRef = _changeDetectorRef;
        this.elementRef = elementRef;
        /** will emit an event with a type of PageEvent everytime the page changes */
        this.page = new EventEmitter();
        this._displayedPageSizeOptions = undefined;
        this.maxPagesIndex = undefined;
        // eslint-disable-next-line @typescript-eslint/naming-convention
        this.DEFAULT_PAGE_SIZE = 50;
        this._pageSizeOptions = [];
        this._color = 'primary';
        this._disabled = false;
        this._pageIndex = 0;
        this._length = 0;
        this._hidePageSize = false;
        this._pageSize = undefined;
        this._showFirstLastButtons = false;
        this._initialized = undefined;
    }
    ngOnInit() {
        this.color = this.color;
        this._initialized = true;
        this._updateDisplayedPageSizeOptions();
        this._changePageSize(this.pageSize, false);
        this._changeDetectorRef.markForCheck();
    }
    nextPage() {
        const previousPageIndex = this.pageIndex;
        this.pageIndex++;
        this._emitPageEvent(previousPageIndex);
    }
    previousPage() {
        const previousPageIndex = this.pageIndex;
        this.pageIndex--;
        this._emitPageEvent(previousPageIndex);
    }
    firstPage() {
        const previousPageIndex = this.pageIndex;
        this.pageIndex = 0;
        this._emitPageEvent(previousPageIndex);
    }
    lastPage() {
        const previousPageIndex = this.pageIndex;
        this.pageIndex = this.maxPagesIndex;
        this._emitPageEvent(previousPageIndex);
    }
    _changePageSize(pageSize, shouldEmit = true) {
        // Current page needs to be updated to reflect the new page size. Navigate to the page
        // containing the previous page's first item.
        const previousPageIndex = this.pageIndex;
        this.pageIndex = Math.floor((this.pageIndex * this.pageSize) / pageSize) || 0;
        this.pageSize = pageSize;
        this.maxPagesIndex = this.length ? Math.ceil(this.length / this.pageSize) - 1 : 0;
        if (shouldEmit) {
            this._emitPageEvent(previousPageIndex);
        }
    }
    _updateDisplayedPageSizeOptions() {
        if (!this._initialized) {
            return;
        }
        // If no page size is provided, use the first page size option or the default page size.
        if (!this.pageSize) {
            this._pageSize = this.pageSizeOptions.length !== 0 ?
                (this.pageSizeOptions[0].value || this.pageSizeOptions[0]) :
                this.DEFAULT_PAGE_SIZE;
        }
        this._displayedPageSizeOptions = this.pageSizeOptions.slice();
        if (this._displayedPageSizeOptions.map(x => x.value).indexOf(this.pageSize) === -1) {
            this._displayedPageSizeOptions.push({ label: this.pageSize, value: this.pageSize });
        }
        // Sort the numbers using a number-specific sort function.
        this._displayedPageSizeOptions.sort((a, b) => (a.value || a) - (b.value || b));
    }
    _emitPageEvent(previousPageIndex) {
        this.page.emit({
            previousPageIndex,
            pageIndex: this.pageIndex,
            pageSize: this.pageSize,
            length: this.length
        });
    }
    _getHostElement() {
        return this.elementRef.nativeElement;
    }
    /* eslint-disable */
    static { this.ngAcceptInputType_disabled = undefined; }
    static { this.ngAcceptInputType_hidePageSize = undefined; }
    static { this.ngAcceptInputType_showFirstLastButtons = undefined; }
    static { this.ngAcceptInputType_pageIndex = undefined; }
    static { this.ngAcceptInputType_length = undefined; }
    static { this.ngAcceptInputType_pageSize = undefined; }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaginatorComponent, deps: [{ token: i0.ChangeDetectorRef }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "17.0.0", version: "18.2.13", type: PaginatorComponent, isStandalone: true, selector: "rs-paginator", inputs: { color: "color", disabled: "disabled", pageIndex: "pageIndex", length: "length", pageSize: "pageSize", pageSizeOptions: "pageSizeOptions", hidePageSize: "hidePageSize", showFirstLastButtons: "showFirstLastButtons" }, outputs: { page: "page" }, ngImport: i0, template: "<div class=\"paginator-outer-container\">\n  <div class=\"paginator-container\">\n    @if (!hidePageSize && length) {\n      <div class=\"paginator-page-size\">\n        <div class=\"paginator-page-size-label\">\n          Items Per Page:\n        </div>\n        @if (_displayedPageSizeOptions.length > 1) {\n          <rs-select [color]=\"color\" [selectValue]=\"pageSize\"\n            [disabled]=\"disabled\" (change)=\"_changePageSize($any($event))\" [items]=\"_displayedPageSizeOptions\">\n          </rs-select>\n        }\n        @if (_displayedPageSizeOptions.length <= 1) {\n          <div class=\"paginator-page-size-value\">{{pageSize}}</div>\n        }\n      </div>\n    }\n\n    <div class=\"paginator-range-actions\">\n      <div class=\"paginator-range-label\">\n        @if (length) {\n          {{pageIndex * pageSize + 1 + ' - ' + (length >= (pageIndex + 1) * pageSize ? (pageIndex + 1) * pageSize :\n          length) + ' of ' +\n          length}}\n        }\n        @if (!length) {\n          No Pages Available\n        }\n      </div>\n\n      @if (showFirstLastButtons) {\n        <button rs-button [color]=\"color\" type=\"button\" (click)=\"firstPage()\"\n          [disabled]=\"pageIndex === 0\">\n          <rs-icon>first</rs-icon>\n        </button>\n      }\n\n      <button rs-button type=\"button\" [color]=\"color\" (click)=\"previousPage()\" [disabled]=\"pageIndex === 0\">\n        <rs-icon>previous</rs-icon>\n      </button>\n      <button rs-button type=\"button\" [color]=\"color\" (click)=\"nextPage()\" [disabled]=\"pageIndex === maxPagesIndex\">\n        <rs-icon>next</rs-icon>\n      </button>\n      @if (showFirstLastButtons) {\n        <button rs-button type=\"button\" [color]=\"color\" (click)=\"lastPage()\" [disabled]=\"pageIndex === maxPagesIndex\"\n          >\n          <rs-icon>last</rs-icon>\n        </button>\n      }\n    </div>\n  </div>\n</div>\n", styles: [":host ::ng-deep button.rs-button[type=button]{min-width:auto;padding:0}.paginator{display:block}.paginator-outer-container{display:flex}.paginator-container{display:flex;align-items:baseline;justify-content:flex-end;padding:0 8px;flex-wrap:wrap-reverse;width:100%}.paginator-page-size{display:flex;align-items:baseline;margin-right:8px}[dir=rtl] .paginator-page-size{margin-right:0;margin-left:8px}.paginator-page-size-label{margin:0 4px}.paginator-page-size-select{margin:6px 4px 0;width:56px}.paginator-page-size-select.form-field-appearance-outline,.paginator-page-size-select.form-field-appearance-fill{width:64px}.paginator-range-label{margin:0 32px 0 24px}.paginator-range-actions{display:flex;align-items:center}.paginator-icon{width:28px;fill:currentColor}[dir=rtl] .paginator-icon{transform:rotate(180deg)}\n"], dependencies: [{ kind: "component", type: SelectComponent, selector: "rs-select", inputs: ["autocomplete", "label", "placeholder", "validation", "errors", "appendTo", "hideErrorMessage", "id", "multiple", "searchable", "clearable", "items", "selectValue", "tabindex", "disabled", "color"], outputs: ["change", "close"] }, { kind: "component", type: ButtonComponent, selector: "button[rs-button], button[rs-raised-button], button[rs-outlined-button]", inputs: ["color"] }, { kind: "component", type: IconComponent, selector: "rs-icon" }], changeDetection: i0.ChangeDetectionStrategy.OnPush }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaginatorComponent, decorators: [{
            type: Component,
            args: [{ selector: 'rs-paginator', changeDetection: ChangeDetectionStrategy.OnPush, standalone: true, imports: [SelectComponent, ButtonComponent, IconComponent], template: "<div class=\"paginator-outer-container\">\n  <div class=\"paginator-container\">\n    @if (!hidePageSize && length) {\n      <div class=\"paginator-page-size\">\n        <div class=\"paginator-page-size-label\">\n          Items Per Page:\n        </div>\n        @if (_displayedPageSizeOptions.length > 1) {\n          <rs-select [color]=\"color\" [selectValue]=\"pageSize\"\n            [disabled]=\"disabled\" (change)=\"_changePageSize($any($event))\" [items]=\"_displayedPageSizeOptions\">\n          </rs-select>\n        }\n        @if (_displayedPageSizeOptions.length <= 1) {\n          <div class=\"paginator-page-size-value\">{{pageSize}}</div>\n        }\n      </div>\n    }\n\n    <div class=\"paginator-range-actions\">\n      <div class=\"paginator-range-label\">\n        @if (length) {\n          {{pageIndex * pageSize + 1 + ' - ' + (length >= (pageIndex + 1) * pageSize ? (pageIndex + 1) * pageSize :\n          length) + ' of ' +\n          length}}\n        }\n        @if (!length) {\n          No Pages Available\n        }\n      </div>\n\n      @if (showFirstLastButtons) {\n        <button rs-button [color]=\"color\" type=\"button\" (click)=\"firstPage()\"\n          [disabled]=\"pageIndex === 0\">\n          <rs-icon>first</rs-icon>\n        </button>\n      }\n\n      <button rs-button type=\"button\" [color]=\"color\" (click)=\"previousPage()\" [disabled]=\"pageIndex === 0\">\n        <rs-icon>previous</rs-icon>\n      </button>\n      <button rs-button type=\"button\" [color]=\"color\" (click)=\"nextPage()\" [disabled]=\"pageIndex === maxPagesIndex\">\n        <rs-icon>next</rs-icon>\n      </button>\n      @if (showFirstLastButtons) {\n        <button rs-button type=\"button\" [color]=\"color\" (click)=\"lastPage()\" [disabled]=\"pageIndex === maxPagesIndex\"\n          >\n          <rs-icon>last</rs-icon>\n        </button>\n      }\n    </div>\n  </div>\n</div>\n", styles: [":host ::ng-deep button.rs-button[type=button]{min-width:auto;padding:0}.paginator{display:block}.paginator-outer-container{display:flex}.paginator-container{display:flex;align-items:baseline;justify-content:flex-end;padding:0 8px;flex-wrap:wrap-reverse;width:100%}.paginator-page-size{display:flex;align-items:baseline;margin-right:8px}[dir=rtl] .paginator-page-size{margin-right:0;margin-left:8px}.paginator-page-size-label{margin:0 4px}.paginator-page-size-select{margin:6px 4px 0;width:56px}.paginator-page-size-select.form-field-appearance-outline,.paginator-page-size-select.form-field-appearance-fill{width:64px}.paginator-range-label{margin:0 32px 0 24px}.paginator-range-actions{display:flex;align-items:center}.paginator-icon{width:28px;fill:currentColor}[dir=rtl] .paginator-icon{transform:rotate(180deg)}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }, { type: i0.ElementRef }], propDecorators: { color: [{
                type: Input
            }], disabled: [{
                type: Input
            }], pageIndex: [{
                type: Input
            }], length: [{
                type: Input
            }], pageSize: [{
                type: Input
            }], pageSizeOptions: [{
                type: Input
            }], hidePageSize: [{
                type: Input
            }], showFirstLastButtons: [{
                type: Input
            }], page: [{
                type: Output
            }] } });

class PaginatorModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaginatorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: PaginatorModule, imports: [CommonModule,
            SelectModule,
            ButtonModule,
            IconModule,
            PaginatorComponent], exports: [PaginatorComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaginatorModule, imports: [CommonModule,
            SelectModule,
            ButtonModule,
            IconModule,
            PaginatorComponent] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: PaginatorModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [
                        CommonModule,
                        SelectModule,
                        ButtonModule,
                        IconModule,
                        PaginatorComponent
                    ],
                    exports: [PaginatorComponent]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { PageEvent, PaginatorComponent, PaginatorModule };
